import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import { buildLeadOriginColumns } from './columns';
import { formatDate } from '../../../utils/formatters';

export default function LeadOriginsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['leadOrigins'],
    queryFn: () => customerService.getLeadOrigins().then((res) => res.data),
  });

  const leadOrigins = useMemo(() => data ?? [], [data]);

  const matchesFilters = useCallback((leadOrigin, { searchValue }) => {
    if (!searchValue) return true;
    const needle = searchValue.toLowerCase();
    return (
      leadOrigin.name.toLowerCase().includes(needle) ||
      (leadOrigin.trigger_phrase ?? '').toLowerCase().includes(needle)
    );
  }, []);

  const filteredLeadOrigins = useMemo(
    () => leadOrigins.filter((leadOrigin) => matchesFilters(leadOrigin, { searchValue: search })),
    [leadOrigins, search, matchesFilters],
  );

  const applySelection = useCallback(
    (overrides) => {
      const visibleIds = leadOrigins
        .filter((leadOrigin) => matchesFilters(leadOrigin, { searchValue: overrides.searchValue ?? search }))
        .map((leadOrigin) => leadOrigin.uuid);
      setSelectionModel((prev) => prev.filter((id) => visibleIds.includes(id)));
    },
    [leadOrigins, search, matchesFilters],
  );

  const updateSearch = (value) => {
    setSearch(value);
    applySelection({ searchValue: value });
  };

  const clearFilters = () => {
    setSearch('');
    applySelection({ searchValue: '' });
  };

  const { pending, remove, undo, dismiss, seed } = useUndoDelete({
    onDelete: (uuid) =>
      customerService
        .deleteLeadOrigin(uuid)
        .then(() => queryClient.invalidateQueries({ queryKey: ['leadOrigins'] })),
    onRestore: (uuid) =>
      customerService
        .restoreLeadOrigin(uuid)
        .then(() => queryClient.invalidateQueries({ queryKey: ['leadOrigins'] })),
  });

  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleDeleteOne = (leadOrigin) => {
    remove(leadOrigin.uuid, t('leadOrigins.deleteSuccess'));
    setSelectionModel((prev) => prev.filter((id) => id !== leadOrigin.uuid));
  };

  const handleDeleteSelected = () => {
    const ids = [...selectionModel];
    remove(ids, t('leadOrigins.deleteSuccess'));
    setSelectionModel([]);
  };

  const columns = useMemo(
    () =>
      buildLeadOriginColumns({
        t,
        language: i18n.language,
        onView: (leadOrigin) => navigate(`/lead-origins/${leadOrigin.uuid}`),
        onEdit: (leadOrigin) => navigate(`/lead-origins/${leadOrigin.uuid}/edit`),
        onDelete: handleDeleteOne,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, navigate],
  );

  const header = (
    <PageHeader
      title={t('leadOrigins.listTitle')}
      breadcrumbs={[
        { label: t('leadOrigins.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('leadOrigins.breadcrumb.list') },
      ]}
      actions={
        <Button variant="contained" onClick={() => navigate('/lead-origins/new')}>
          {t('leadOrigins.newLeadOrigin')}
        </Button>
      }
    />
  );

  const selectedCount = selectionModel.length;

  // Plain props object, NOT a function/component: `slots.toolbar` must stay a
  // stable reference (the imported ListFilterToolbar) across renders. Handing
  // DataGrid a freshly-defined render function every render — even one that
  // just wraps the same component — makes React treat it as a different
  // component type each time, remounting the toolbar (and its focused search
  // input) after every keystroke.
  //
  // Lead origins have no domain enum to filter on, so `filters` stays empty
  // and search carries the whole toolbar. Search is the only filter here, so
  // it also has to be the thing that puts a chip in the ActiveFiltersBar —
  // that bar renders nothing while `chips` is empty, which would hide the
  // result count and "Clear" entirely.
  const chips =
    search === ''
      ? []
      : [
          {
            key: 'search',
            label: `${t('leadOrigins.filters.search')}: ${search}`,
            onDelete: () => updateSearch(''),
          },
        ];

  const toolbarProps = {
    filters: [],
    search: { label: t('leadOrigins.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('leadOrigins.filters.resultsFound', { count: filteredLeadOrigins.length }),
    clearLabel: t('leadOrigins.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
    bulkAction: selectedCount > 0 && (
      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteSelected}>
        {t('leadOrigins.list.deleteSelected', { count: selectedCount })}
      </Button>
    ),
  };

  return (
    <BoundedPageLayout header={header}>
      <DataGridCard>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {filteredLeadOrigins.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {filteredLeadOrigins
                  .slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize,
                  )
                  .map((leadOrigin) => (
                    <ListItem
                      key={leadOrigin.uuid}
                      divider
                      secondaryAction={
                        <RowActionsMenu
                          actions={[
                            { label: t('leadOrigins.actions.view'), icon: <VisibilityOutlinedIcon fontSize="small" />, onClick: () => navigate(`/lead-origins/${leadOrigin.uuid}`) },
                            { label: t('leadOrigins.actions.edit'), icon: <EditOutlinedIcon fontSize="small" />, onClick: () => navigate(`/lead-origins/${leadOrigin.uuid}/edit`) },
                            { label: t('leadOrigins.actions.delete'), icon: <DeleteOutlineIcon fontSize="small" />, color: 'error', onClick: () => handleDeleteOne(leadOrigin) },
                          ]}
                        />
                      }
                    >
                      <Checkbox
                        edge="start"
                        checked={selectionModel.includes(leadOrigin.uuid)}
                        onChange={(event) =>
                          setSelectionModel((prev) =>
                            event.target.checked ? [...prev, leadOrigin.uuid] : prev.filter((id) => id !== leadOrigin.uuid),
                          )
                        }
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={leadOrigin.name}
                        secondary={
                          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            {leadOrigin.trigger_phrase && (
                              <Typography variant="caption" color="text.secondary">
                                {leadOrigin.trigger_phrase}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {t('leadOrigins.list.leadCount', { count: leadOrigin.lead_count ?? 0 })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(leadOrigin.created_at, i18n.language)}
                            </Typography>
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        ) : (
          <DataGrid
            // The trigger phrase column wraps to a second line for longer
            // text; a fixed density-based row height clips it in compact
            // mode, so let each row size to its own content.
            getRowHeight={() => 'auto'}
            rows={filteredLeadOrigins}
            columns={columns}
            getRowId={(row) => row.uuid}
            loading={isLoading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
            }}
            localeText={{
              toolbarColumns: t('leadOrigins.filters.columns'),
              toolbarFilters: t('leadOrigins.filters.filters'),
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>

      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('leadOrigins.deleteUndo')} />

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('leadOrigins.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('leadOrigins.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
